// frontend/src/components/editors/IndicatorEditorRolling.tsx
import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from 'react';
import { useSeriesOptions } from '../../utils/useSeriesOptions';
import { InstrumentPill, ValuePill, FromIndicatorBadge } from './IndicatorPreviewFields';

export type RollingOperation = 'min' | 'max' | 'stddev' | 'stddevp';

export type IndicatorEditorRollingRef = {
  getConfig: () => {
    instrumentId: string;
    field: string;
    period: number;
    lag: number;
    operation: RollingOperation;
  };
};

type Props = {
  config: {
    instrumentId: string;
    field?: string;
    period?: number;
    lag?: number;
    operation?: RollingOperation;
  };
  onChange: (config: Props['config']) => void;
  editMode: boolean;
  indicatorId?: string;
};

const IndicatorEditorRolling = forwardRef<IndicatorEditorRollingRef, Props>(
  ({ config, onChange, editMode, indicatorId }, ref) => {
    const [period, setPeriod] = useState(config.period ?? 20);
    const [lag, setLag] = useState(config.lag ?? 0);

    const allSeriesOptions = useSeriesOptions(indicatorId);
    const hasSingleOption = allSeriesOptions.length === 1;

    useEffect(() => {
      if (hasSingleOption && config.instrumentId !== allSeriesOptions[0].id) {
        onChange({ ...config, instrumentId: allSeriesOptions[0].id });
      }
    }, [hasSingleOption, allSeriesOptions, config.instrumentId, onChange]);

    const selectedSeries = allSeriesOptions.find(s => s.id === config.instrumentId);
    const availableFields = selectedSeries
      ? Object.keys(selectedSeries.emittedFields).filter(
        f => selectedSeries.emittedFields[f] === 'Number'
      )
      : [];

    const currentField = config.field && availableFields.includes(config.field)
      ? config.field
      : availableFields.includes('Close')
        ? 'Close'
        : availableFields[0] || 'Value';

    const currentOperation: RollingOperation =
      config.operation === 'max' || config.operation === 'stddev' || config.operation === 'stddevp'
        ? config.operation
        : 'min';

    useEffect(() => {
      if (config.field !== currentField) {
        onChange({ ...config, field: currentField });
      }
    }, [currentField, config, onChange]);

    useEffect(() => {
      if (config.operation !== currentOperation) {
        onChange({ ...config, operation: currentOperation });
      }
    }, [currentOperation, config, onChange]);

    const handleSeriesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange({ ...config, instrumentId: e.target.value });
    };

    const handlePeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value);
      if (!isNaN(val) && val > 0) {
        setPeriod(val);
        onChange({ ...config, period: val });
      }
    };

    const handleLagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value);
      if (!isNaN(val) && val >= 0) {
        setLag(val);
        onChange({ ...config, lag: val });
      }
    };

    useImperativeHandle(ref, () => ({
      getConfig: () => ({
        instrumentId: config.instrumentId,
        field: currentField,
        period,
        lag,
        operation: currentOperation,
      }),
    }));

    if (!editMode) {
      const label = selectedSeries?.label || '???';
      const opLabel =
        currentOperation === 'max'
          ? 'Max'
          : currentOperation === 'stddev'
            ? 'Std Dev (Sample)'
            : currentOperation === 'stddevp'
              ? 'Std Dev (Population)'
              : 'Min';
      const fieldLabel = currentField.charAt(0).toUpperCase() + currentField.slice(1);
      return (
        <div className="flex flex-wrap gap-2 items-center text-sm">
          <InstrumentPill>{label}</InstrumentPill>
          <ValuePill label="Operation" value={opLabel} />
          <ValuePill label="Period" value={period} />
          <ValuePill label="Field" value={fieldLabel} />
          {lag > 0 && <ValuePill label="Lag" value={lag} />}
          {selectedSeries?.type === 'indicator' && <FromIndicatorBadge />}
        </div>
      );
    }

    return (
      <div className="flex flex-wrap gap-4 items-center">
        {!hasSingleOption && (
          <label>
            Series:
            <select
              value={config.instrumentId}
              onChange={handleSeriesChange}
              className="ml-2 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm"
            >
              {allSeriesOptions.map(opt => (
                <option key={opt.id} value={opt.id}>
                  {opt.label} {opt.type === 'indicator' ? ' (indicator)' : ''}
                </option>
              ))}
            </select>
          </label>
        )}

        {availableFields.length > 1 && (
          <label>
            Field:
            <select
              value={currentField}
              onChange={e => onChange({ ...config, field: e.target.value })}
              className="ml-2 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm"
            >
              {availableFields.map(f => (
                <option key={f} value={f}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </option>
              ))}
            </select>
          </label>
        )}

        <label>
          Function:
          <select
            value={currentOperation}
            onChange={e => onChange({ ...config, operation: e.target.value as RollingOperation })}
            className="ml-2 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm"
          >
            <option value="min">Min</option>
            <option value="max">Max</option>
            <option value="stddev">Std Dev (Sample)</option>
            <option value="stddevp">Std Dev (Population)</option>
          </select>
        </label>

        <label>
          Period:
          <input
            type="number"
            min="1"
            value={period}
            onChange={handlePeriodChange}
            className="ml-2 w-20 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark"
          />
        </label>

        <label>
          Lag:
          <input
            type="number"
            min="0"
            value={lag}
            onChange={handleLagChange}
            className="ml-2 w-20 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark"
          />
        </label>
      </div>
    );
  }
);

IndicatorEditorRolling.displayName = 'IndicatorEditorRolling';

export default IndicatorEditorRolling;